import { Field, FieldProps, useFormikContext } from 'formik';
import { DataPlaceholder } from '../../../../components';
import { formatDate } from '../../../../utils';
import { ParticipantProps } from '../../../../utils/types';
import { BankIdConfirmationFormData, BankIdConfirmationFormFields } from '../useBankIdConfirmation';
import DatePicker from 'react-date-picker';
import dayjs from 'dayjs';
import { FormikTextInput, Label, Error, type SelectOptionProps, Select } from '@simplea/shared-ui';
import { IdCardType } from '../utils';

type Props = {
    participant: ParticipantProps;
    hasIssueDate: boolean | '' | null | undefined;
    hasIssuer: boolean;
    idCardCountryCode: string | undefined;
    countries: SelectOptionProps[];
    idType: string | undefined;
    idCardKindCode: string;
};

export const IdCard = ({
    participant,
    hasIssueDate,
    hasIssuer,
    idCardCountryCode,
    countries,
    idType,
    idCardKindCode,
}: Props) => {
    const { errors, validateField, touched } = useFormikContext<BankIdConfirmationFormData>();

    const idCountry = idCardCountryCode ? (
        <DataPlaceholder label="Země vydání">
            {countries.find((country) => country.value === idCardCountryCode)?.label}
        </DataPlaceholder>
    ) : (
        <div>
            <Label label="Země vydání">
                <Field
                    id={BankIdConfirmationFormFields.IdCardCountry}
                    name={BankIdConfirmationFormFields.IdCardCountry}
                    options={countries}
                    component={Select}
                    validate={(value: string) => validateField(value)}
                    error={errors.idCardCountry}
                />
            </Label>

            <Error isDisplayed={!!errors.idCardCountry && touched.idCardCountry}>
                {errors.idCardCountry as string}
            </Error>
        </div>
    );

    return (
        <>
            <div className="confirmationItemCentered mobileMargin">
                <h3>Doklad</h3>
            </div>
            <DataPlaceholder label="Typ dokladu">{idType}</DataPlaceholder>
            <DataPlaceholder label="Číslo dokladu">{participant?.IdCards[0].IdCardNo}</DataPlaceholder>
            {hasIssueDate ? (
                <DataPlaceholder label="Vydán kdy" className="mobileMargin">
                    {formatDate(participant?.IdCards[0].IssuedDt)}
                </DataPlaceholder>
            ) : (
                <Label htmlFor={BankIdConfirmationFormFields.IssueDate} label="Vydán kdy" className="mobileMargin">
                    {/* TODO: put DatePicker into component prop to inherit all props */}
                    <Field
                        id={BankIdConfirmationFormFields.IssueDate}
                        name={BankIdConfirmationFormFields.IssueDate}
                        error={errors.issueDate}
                    >
                        {({ field, form }: FieldProps) => (
                            <DatePicker
                                {...field}
                                name={BankIdConfirmationFormFields.IssueDate}
                                onChange={async (value: Date) => {
                                    await form.setFieldValue(field.name, value);
                                }}
                                format="dd-MM-yyyy"
                                disableCalendar
                                dayPlaceholder="dd"
                                monthPlaceholder="mm"
                                yearPlaceholder="rrrr"
                                maxDate={dayjs().year(9999).toDate()}
                                locale="cs-CZ"
                                required
                                className={errors.issueDate ? 'issueDateError' : ''}
                            />
                        )}
                    </Field>
                    <Error isDisplayed={!!errors.issueDate}>{errors.issueDate}</Error>
                </Label>
            )}
            <DataPlaceholder label="Platnost do">{formatDate(participant?.IdCards[0].ExpiryDt)}</DataPlaceholder>
            {hasIssuer ? (
                <DataPlaceholder label="Vydán kým">{participant?.IdCards[0].Issuer}</DataPlaceholder>
            ) : (
                <div>
                    <FormikTextInput
                        label="Vydán kým"
                        name={BankIdConfirmationFormFields.Issuer}
                        className="mobileMargin"
                        error={errors.issuer}
                    />
                </div>
            )}
            {!idCardCountryCode && idCardKindCode === IdCardType.PP ? (
                <DataPlaceholder label="Země vydání">Česká republika</DataPlaceholder>
            ) : (
                idCountry
            )}
        </>
    );
};
